<template>
    <div>
        <!-- ======================== MODE SIMPLE ======================== -->
        <template v-if="simple">

            <!-- Type Cards -->
            <div>
                <label class="text-sm font-medium text-gray-700 block mb-2">Type d'appareil</label>

                <!-- Loading skeleton -->
                <div v-if="loadingCategories" class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div v-for="i in 3" :key="'sk-' + i" class="h-20 rounded-xl bg-gray-100"></div>
                </div>

                <!-- Category cards -->
                <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <button
                        v-for="cat in categories"
                        :key="'cat-app-' + cat.id"
                        type="button"
                        @click="selectTypeCard(cat)"
                        :class="[
                            'flex flex-col items-center justify-center gap-2 py-4 px-3 rounded-xl border-2 transition-all duration-150 text-sm font-medium focus:outline-none',
                            dataModel.type === cat.key
                                ? 'border-red-500 bg-red-50 text-red-700 shadow-sm'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:bg-red-50 hover:text-red-600'
                        ]"
                    >
                        <img :src="categoryIcon(cat)" alt="" class="w-8 h-8 object-contain">
                        <span class="text-center leading-tight">{{ cat.label }}</span>
                    </button>
                </div>
            </div>

            <!-- Infos complémentaires accordion -->
            <div class="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                <button
                    type="button"
                    @click="showExtraFields = !showExtraFields"
                    class="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium text-gray-700 focus:outline-none"
                >
                    <span class="flex items-center gap-2">
                        <i class="fas fa-sliders-h text-gray-400"></i>
                        Informations complémentaires
                        <span v-if="hasExtraData" class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs leading-none">
                            {{ extraFieldsCount }}
                        </span>
                    </span>
                    <svg
                        :class="['w-4 h-4 text-gray-400 transition-transform duration-200', showExtraFields ? 'rotate-180' : '']"
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>

                <div v-show="showExtraFields" class="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <!-- Marque -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Marque</label>
                        <div class="relative mt-1">
                            <i class="fas fa-tag absolute left-3 top-3 text-gray-400 z-10"></i>
                            <input
                                v-model="dataModel.marque"
                                @input="onMarqueInput"
                                @focus="showMarqueDropdown = true"
                                @blur="hideMarqueDropdown"
                                type="text"
                                placeholder="Ex: Daikin"
                                :disabled="!dataModel.type"
                                class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2 disabled:bg-gray-50 disabled:text-gray-400"
                            />
                            <div v-if="showMarqueDropdown && filteredMarques.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                <div v-for="marque in filteredMarques" :key="'marque-' + marque" @mousedown="selectMarque(marque)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ marque }}</div>
                            </div>
                            <div v-if="loadingMarques" class="absolute right-3 top-3">
                                <div class="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Modèle -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Modèle</label>
                        <div class="relative mt-1">
                            <i class="fas fa-tag absolute left-3 top-3 text-gray-400 z-10"></i>
                            <input
                                v-model="dataModel.model"
                                @input="onModelInput"
                                @focus="showModelDropdown = true"
                                @blur="hideModelDropdown"
                                type="text"
                                placeholder="Ex: XYZ123"
                                :disabled="!dataModel.marque"
                                class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2 disabled:bg-gray-50 disabled:text-gray-400"
                            />
                            <div v-if="showModelDropdown && filteredModeles.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                <div v-for="model in filteredModeles" :key="'model-' + model.id" @mousedown="selectModel(model)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ model.modele }}</div>
                            </div>
                            <div v-if="loadingModeles" class="absolute right-3 top-3">
                                <div class="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                            </div>
                        </div>
                    </div>

                    <!-- Numéro de série -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Numéro de série</label>
                        <div class="relative mt-1">
                            <i class="fas fa-hashtag absolute left-3 top-3 text-gray-400"></i>
                            <input v-model="dataModel.numero_serie" type="text" placeholder="Ex: 123456789" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"/>
                        </div>
                    </div>

                    <!-- Date d'installation -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Date d'installation</label>
                        <div class="relative mt-1">
                            <i class="fas fa-calendar-alt absolute left-3 top-3 text-gray-400"></i>
                            <input v-model="dataModel.installation_date" type="date" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"/>
                        </div>
                    </div>

                    <!-- Emplacement -->
                    <div>
                        <label class="text-sm font-medium text-gray-700">Emplacement</label>
                        <div class="relative mt-1">
                            <i class="fas fa-map-marker-alt absolute left-3 top-3 text-gray-400 z-10"></i>
                            <input
                                v-model="dataModel.emplacement"
                                @input="onEmplacementInput"
                                @focus="showEmplacementDropdown = true"
                                @blur="hideEmplacementDropdown"
                                type="text"
                                placeholder="Ex: Salon"
                                class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"
                            />
                            <div v-if="showEmplacementDropdown && filteredEmplacements.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                <div v-for="emplacement in filteredEmplacements" :key="'emplacement-' + emplacement" @mousedown="selectEmplacement(emplacement)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ emplacement }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Notes -->
                    <div class="col-span-1 md:col-span-2">
                        <label class="text-sm font-medium text-gray-700">Notes internes concernant l'appareil</label>
                        <div class="relative mt-1">
                            <i class="fas fa-sticky-note absolute left-3 top-3 text-gray-400"></i>
                            <textarea v-model="dataModel.note" placeholder="Exemple: appareil dangereux" rows="3" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"></textarea>
                        </div>
                    </div>
                </div>
            </div>

        </template>

        <!-- ======================== MODE NORMAL ======================== -->
        <template v-else>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

                <!-- Type Select -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Type</label>
                    <div class="relative mt-1">
                        <i class="fas fa-thermometer-half absolute left-3 top-3 text-gray-400 z-10 pointer-events-none"></i>
                        <select
                            v-model="dataModel.type"
                            @change="onTypeChange"
                            class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2 appearance-none bg-white"
                        >
                            <option value="" disabled>Choisir un type</option>
                            <option v-for="cat in categories" :key="cat.key" :value="cat.key">{{ cat.label }}</option>
                        </select>
                        <svg class="w-4 h-4 absolute right-3 top-3 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                        </svg>
                        <div v-if="loadingCategories" class="absolute right-10 top-3">
                            <div class="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                        </div>
                    </div>
                </div>

                <!-- Marque -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Marque</label>
                    <div class="relative mt-1">
                        <i class="fas fa-tag absolute left-3 top-3 text-gray-400 z-10"></i>
                        <input
                            v-model="dataModel.marque"
                            @input="onMarqueInput"
                            @focus="showMarqueDropdown = true"
                            @blur="hideMarqueDropdown"
                            type="text"
                            placeholder="Ex: Daikin"
                            :disabled="!dataModel.type"
                            class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2 disabled:bg-gray-50 disabled:text-gray-400"
                        />
                        <div v-if="showMarqueDropdown && filteredMarques.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <div v-for="marque in filteredMarques" :key="'marque-' + marque" @mousedown="selectMarque(marque)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ marque }}</div>
                        </div>
                        <div v-if="loadingMarques" class="absolute right-3 top-3">
                            <div class="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                        </div>
                    </div>
                </div>

                <!-- Modèle -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Modèle</label>
                    <div class="relative mt-1">
                        <i class="fas fa-tag absolute left-3 top-3 text-gray-400 z-10"></i>
                        <input
                            v-model="dataModel.model"
                            @input="onModelInput"
                            @focus="showModelDropdown = true"
                            @blur="hideModelDropdown"
                            type="text"
                            placeholder="Ex: XYZ123"
                            :disabled="!dataModel.marque"
                            class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2 disabled:bg-gray-50 disabled:text-gray-400"
                        />
                        <div v-if="showModelDropdown && filteredModeles.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <div v-for="model in filteredModeles" :key="'model-' + model.id" @mousedown="selectModel(model)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ model.modele }}</div>
                        </div>
                        <div v-if="loadingModeles" class="absolute right-3 top-3">
                            <div class="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                        </div>
                    </div>
                </div>

                <!-- Numéro de série -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Numéro de série</label>
                    <div class="relative mt-1">
                        <i class="fas fa-hashtag absolute left-3 top-3 text-gray-400"></i>
                        <input v-model="dataModel.numero_serie" type="text" placeholder="Ex: 123456789" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"/>
                    </div>
                </div>

                <!-- Date d'installation -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Date d'installation</label>
                    <div class="relative mt-1">
                        <i class="fas fa-calendar-alt absolute left-3 top-3 text-gray-400"></i>
                        <input v-model="dataModel.installation_date" type="date" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"/>
                    </div>
                </div>

                <!-- Emplacement -->
                <div>
                    <label class="text-sm font-medium text-gray-700">Emplacement</label>
                    <div class="relative mt-1">
                        <i class="fas fa-map-marker-alt absolute left-3 top-3 text-gray-400 z-10"></i>
                        <input
                            v-model="dataModel.emplacement"
                            @input="onEmplacementInput"
                            @focus="showEmplacementDropdown = true"
                            @blur="hideEmplacementDropdown"
                            type="text"
                            placeholder="Ex: Salon"
                            class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"
                        />
                        <div v-if="showEmplacementDropdown && filteredEmplacements.length > 0" class="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                            <div v-for="emplacement in filteredEmplacements" :key="'emplacement-' + emplacement" @mousedown="selectEmplacement(emplacement)" class="px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">{{ emplacement }}</div>
                        </div>
                    </div>
                </div>

                <!-- Notes -->
                <div class="col-span-1 md:col-span-2">
                    <label class="text-sm font-medium text-gray-700">Notes interne</label>
                    <div class="relative mt-1">
                        <i class="fas fa-sticky-note absolute left-3 top-3 text-gray-400"></i>
                        <textarea v-model="dataModel.note" placeholder="Notes internes sur l'appareil" rows="3" class="pl-10 w-full rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-red-500 text-sm py-2"></textarea>
                    </div>
                </div>

            </div>
        </template>
    </div>
</template>

<script>
export default {
    props: {
        value: {
            type: Object,
            default: () => ({})
        },
        simple: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            dataModel: { ...this.value },

            // Autocomplete data
            categories: [],
            allMarques: [],
            allModeles: [],
            emplacements: [
                'Salon', 'Cuisine', 'Chambre', 'Bureau', 'Garage',
                'Sous-sol', 'Grenier', 'Salle de bain', 'Couloir', 'Entrée',
                'Extérieur', 'Cave', 'Véranda', 'Balcon', 'Terrasse',
                'Local technique', 'Dépendance', 'Atelier', 'Salle de jeux',
                'Salle de sport', 'Piscine', 'Abri de jardin', 'Chaufferie',
                'Toit', 'Cellier'
            ],

            // Loading states
            loadingCategories: false,
            loadingMarques: false,
            loadingModeles: false,

            // Dropdown visibility
            showMarqueDropdown: false,
            showModelDropdown: false,
            showEmplacementDropdown: false,

            // Mode simple — accordion état
            showExtraFields: false,
        };
    },

    computed: {
        filteredMarques() {
            if (!this.dataModel.marque) return this.allMarques;
            const search = this.dataModel.marque.toLowerCase();
            return this.allMarques.filter(m => m.toLowerCase().includes(search));
        },
        filteredModeles() {
            if (!this.dataModel.model) return this.allModeles;
            const search = this.dataModel.model.toLowerCase();
            return this.allModeles.filter(m => m && m.modele && m.modele.toLowerCase().includes(search));
        },
        filteredEmplacements() {
            if (!this.dataModel.emplacement) return this.emplacements;
            const search = this.dataModel.emplacement.toLowerCase();
            return this.emplacements.filter(e => e.toLowerCase().includes(search));
        },

        // Nombre de champs complémentaires remplis (badge sur l'accordion)
        extraFieldsCount() {
            let count = 0;
            if (this.dataModel.marque) count++;
            if (this.dataModel.model) count++;
            if (this.dataModel.numero_serie) count++;
            if (this.dataModel.installation_date) count++;
            if (this.dataModel.emplacement) count++;
            if (this.dataModel.note) count++;
            return count;
        },
        hasExtraData() {
            return this.extraFieldsCount > 0;
        }
    },

    methods: {
        // Retourne une icône emoji pour une catégorie
        categoryIcon(cat) {
            return cat.icon;
        },

        // Sélection d'une carte de type (mode simple)
        async selectTypeCard(cat) {
            if (this.dataModel.type === cat) return; // déjà sélectionné
            this.dataModel.type = cat.key;
            this.dataModel.marque = '';
            this.dataModel.model = '';
            this.allMarques = [];
            this.allModeles = [];
            this.emitChange();
            this.$forceUpdate()
            await this.loadMarques();
        },

        async loadCategories() {
            this.loadingCategories = true;
            try {
                const response = await this.$request.post('/reference/get-categories', {});
                
                this.categories = response || [];
                // En mode normal on présélectionne le premier ; en mode simple on laisse l'utilisateur choisir
                if (!this.simple && !this.dataModel.type) {
                    if (this.categories.length > 0) {
                        this.dataModel.type = this.categories[0].key || '';
                    }
                    if (this.dataModel.type) {
                        this.loadMarques();
                    }
                }
            } catch (e) {
                console.error(e);
            }
            this.loadingCategories = false;
        },

        async onTypeChange() {
            this.dataModel.marque = '';
            this.dataModel.model = '';
            this.allMarques = [];
            this.allModeles = [];
            this.emitChange();
            if (this.dataModel.type) {
                await this.loadMarques();
            }
        },

        async loadMarques() {
            if (!this.dataModel.type) return;
            this.loadingMarques = true;
            try {
                const response = await this.$request.post('/reference/get-marques', { categorie: this.dataModel.type });
                this.allMarques = response || [];
            } catch (e) {
                console.error(e);
            }
            this.loadingMarques = false;
        },

        onMarqueInput() {
            this.showMarqueDropdown = true;
            this.emitChange();
        },

        async selectMarque(marque) {
            this.dataModel.marque = marque;
            this.showMarqueDropdown = false;
            this.dataModel.model = '';
            this.allModeles = [];
            await this.loadModeles();
        },

        hideMarqueDropdown() {
            setTimeout(() => { this.showMarqueDropdown = false; }, 200);
        },

        async loadModeles() {
            if (!this.dataModel.type || !this.dataModel.marque) return;
            this.loadingModeles = true;
            try {
                const response = await this.$request.post('/reference/get-modeles', {
                    categorie: this.dataModel.type,
                    marque: this.dataModel.marque
                });
                this.allModeles = response || [];
            } catch (e) {
                console.error(e);
            }
            this.loadingModeles = false;
        },

        onModelInput() {
            this.showModelDropdown = true;
            this.emitChange();
        },

        selectModel(model) {
            this.dataModel.model = model.modele;
            this.showModelDropdown = false;
            this.emitChange();
        },

        hideModelDropdown() {
            setTimeout(() => { this.showModelDropdown = false; }, 200);
        },

        onEmplacementInput() {
            this.showEmplacementDropdown = true;
        },

        selectEmplacement(emplacement) {
            this.dataModel.emplacement = emplacement;
            this.showEmplacementDropdown = false;
            this.emitChange();
        },

        hideEmplacementDropdown() {
            setTimeout(() => { this.showEmplacementDropdown = false; }, 200);
        },

        emitChange() {
            this.$emit('input', { ...this.dataModel });
        }
    },

    watch: {
        value: {
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.dataModel)) {
                    this.dataModel = { ...newValue };
                }
            },
            deep: true
        },
        dataModel: {
            handler(newValue) {
                if (JSON.stringify(newValue) !== JSON.stringify(this.value)) {
                    this.$emit('input', { ...newValue });
                }
            },
            deep: true
        },
    },

    mounted() {
        this.loadCategories();
    }
}
</script>